<template>
  <Layout>
    <Head :title="title" />

    <div class="p-4 sm:p-6 md:p-8">
      <div class="space-y-6">
        <div class="border-b pb-4">
          <h1 class="text-2xl font-bold text-gray-900">{{ invite.appointment_type_name || invite.legal_case_type_name }}</h1>
          <p class="text-gray-600 mt-1">
            {{ t('common.successfully_scheduled') }}
          </p>
        </div>

        <!-- Cancellation notice -->
        <div v-if="isCanceled" class="bg-red-50 border border-red-300 rounded-md p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">
                This appointment has been canceled
              </h3>
              <div class="mt-2 text-sm text-red-700">
                <p>
                  Please contact your attorney for further information or to reschedule.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Rescheduled notice -->
        <div v-if="isRescheduled" class="bg-yellow-50 border border-yellow-300 rounded-md p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-yellow-800">
                This appointment has been rescheduled
              </h3>
              <div class="mt-2 text-sm text-yellow-700">
                <p>
                  Your appointment has been updated to a new date and time. Please review the details below.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Show different messages based on appointment status -->
        <div v-if="!isAppointmentPast && !isCanceled && !isRescheduled" class="bg-green-50 border border-green-200 rounded-md p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <div class="mt-2 text-sm text-green-700">
                <p>
                  {{ t('common.make_note') }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Past appointment notice -->
        <div v-if="isAppointmentPast" class="bg-gray-50 border border-gray-200 rounded-md p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.707-10.293a1 1 0 00-1.414-1.414L9 6.586 7.707 5.293a1 1 0 00-1.414 1.414L8.586 9l-2.293 2.293a1 1 0 101.414 1.414L9 10.414l2.293 2.293a1 1 0 001.414-1.414L10.414 9l2.293-2.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <div class="mt-2 text-sm text-gray-700">
                <p>
                  {{ t('appointments.past_appointment_notice') }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Show appointment details -->
        <template v-if="!isAppointmentPast || isCanceled || isRescheduled">
          <!-- Appointment Details -->
          <Card>
            <CardHeader>
              <CardTitle>{{ t('appointments.appointment_details') }}</CardTitle>
              <CardDescription>{{ invite.legal_case_type_description }}</CardDescription>
            </CardHeader>
            <CardContent>
              <div class="space-y-4">
                <!-- Appointment Date and Time -->
                <div v-if="invite.slot_start_datetime && invite.slot_end_datetime" class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div class="space-y-2">
                    <div class="flex items-center gap-2 text-blue-800">
                      <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span class="font-semibold text-lg">{{ formatDateTime(invite.slot_start_datetime) }}</span>
                    </div>
                    <div class="flex items-center gap-2 text-blue-700 text-sm">
                      <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{{ t('appointments.ends') }} {{ formatDateTime(invite.slot_end_datetime) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Other Details -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div v-if="invite.location" class="flex items-start gap-2 text-gray-600">
                    <svg class="h-4 w-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div>
                      <span class="font-medium">{{ t('common.location') }}</span>
                      <span class="ml-1">{{ invite.location }}</span>
                    </div>
                  </div>

                  <div v-if="invite.primary_attorney_name" class="flex items-start gap-2 text-gray-600">
                    <svg class="h-4 w-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <div>
                      <span class="font-medium">{{ t('common.primary_attorney') }}</span>
                      <span class="ml-1">{{ invite.primary_attorney_name }}</span>
                      <span v-if="invite.primary_attorney_email" class="block text-xs text-gray-500">
                        {{ invite.primary_attorney_email }}
                      </span>
                    </div>
                  </div>

                  <div v-if="invite.booking_date" class="flex items-start gap-2 text-gray-600">
                    <svg class="h-4 w-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <span class="font-medium">{{ t('appointments.booked_on') }}</span>
                      <span class="ml-1">{{ formatDate(invite.booking_date) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <!-- Contact Information -->
          <Card v-if="invite.primary_attorney_name || invite.primary_attorney_email">
            <CardHeader>
              <CardTitle>{{ t('common.need_help') }}</CardTitle>
              <CardDescription>
                {{ t('common.make_changes') }}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div class="flex items-start gap-3 text-sm">
                <svg class="h-5 w-5 mt-0.5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <div>
                  <p class="font-medium text-gray-900">{{ invite.primary_attorney_name }}</p>
                  <p v-if="invite.primary_attorney_email" class="text-gray-600">{{ invite.primary_attorney_email }}</p>
                  <p class="mt-2 text-gray-500">
                    {{ t('common.contact_attorney') }}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </template>
      </div>
    </div>
  </Layout>
</template>

<script setup>
import { Head } from '@inertiajs/vue3'
import { computed } from 'vue'

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import Layout from '@/Layouts/Minimal.vue'
import { t } from '@/entrypoints/translations.js'

// Props
const { invite } = defineProps({
  invite: {
    type: Object,
    required: true
  },
})

// Instance Variables
const title = `${invite.appointment_type_name || invite.legal_case_type_name} - Scheduled Appointment - Kiosk`

// Check appointment status
const isCanceled = computed(() => invite.scheduling_status === 'C')
const isRescheduled = computed(() => invite.scheduling_status === 'R')
const isAppointmentPast = computed(() => {
  if (!invite.slot_end_datetime) return false
  return new Date(invite.slot_end_datetime) < new Date()
})

// Date formatting functions
const formatDateTime = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long', 
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}
</script>
